import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ProposalDialog, type ProposalValues } from '@/components/proposal-dialog';
import { ResponsiveTable } from '@/components/responsive-table';
import { RowThumb } from '@/components/row-thumb';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProposals } from '@/lib/api-hooks';
import { proposalFilters, proposalStatusStyles } from '@/lib/proposal-status';
import {
  PROPOSAL_PAGE_SIZE_OPTIONS,
  type Proposal,
  type ProposalOrderKey,
  archiveProposal,
  createProposal,
  restoreProposal,
  updateProposal,
} from '@/lib/proposals';
import { useProposalsStore } from '@/lib/proposals-store';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

const orderOptions: Array<{ value: ProposalOrderKey; label: string }> = [
  { value: 'newest', label: 'Recently updated' },
  { value: 'title', label: 'Title (A–Z)' },
  { value: 'votes-desc', label: 'Most votes' },
  { value: 'votes-asc', label: 'Fewest votes' },
];

export function ProposalsPage() {
  const search = useProposalsStore((state) => state.search);
  const status = useProposalsStore((state) => state.status);
  const order = useProposalsStore((state) => state.order);
  const page = useProposalsStore((state) => state.page);
  const pageSize = useProposalsStore((state) => state.pageSize);
  const setSearch = useProposalsStore((state) => state.setSearch);
  const setStatus = useProposalsStore((state) => state.setStatus);
  const setOrder = useProposalsStore((state) => state.setOrder);
  const setPage = useProposalsStore((state) => state.setPage);
  const setPageSize = useProposalsStore((state) => state.setPageSize);

  const { data, isLoading } = useProposals({ search, status, order, page, pageSize });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Proposal | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(proposal: Proposal) {
    setEditing(proposal);
    setDialogOpen(true);
  }

  async function handleSubmit(values: ProposalValues) {
    if (editing) {
      await updateProposal(editing.id, values);
    } else {
      await createProposal(values);
    }
    setPage(1);
    invalidateResource('proposal');
  }

  async function handleArchive(proposal: Proposal) {
    await archiveProposal(proposal.id);
    invalidateResource('proposal');
  }

  async function handleRestore(proposal: Proposal) {
    await restoreProposal(proposal.id);
    invalidateResource('proposal');
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search proposals"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-muted-foreground text-sm sm:inline">Order by</span>
          <Select value={order} onValueChange={(value) => setOrder(value as ProposalOrderKey)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={openCreate}>
            <Plus />
            New proposal
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {proposalFilters.map((filter) => (
          <Button
            key={filter}
            size="sm"
            variant={status === filter ? 'default' : 'ghost'}
            onClick={() => setStatus(filter)}
            className="h-8"
          >
            {filter}
          </Button>
        ))}
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading proposals…"
          skeletonRows={pageSize}
          emptyLabel="No proposals match your filters."
          rows={items}
          rowKey={(proposal) => proposal.id}
          columns={[
            {
              key: 'proposal',
              header: 'Proposal',
              title: true,
              cell: (proposal: Proposal) => (
                <div className="flex items-center gap-3">
                  <RowThumb media={proposal.coverMedia} label={proposal.title} />
                  <Link to={`/proposals/${proposal.slug}`} className="font-medium hover:underline">
                    {proposal.title}
                  </Link>
                  {proposal.archived && (
                    <Badge
                      variant="outline"
                      className="border-transparent bg-muted text-muted-foreground"
                    >
                      Archived
                    </Badge>
                  )}
                </div>
              ),
            },
            {
              key: 'artist',
              header: 'Artist',
              cell: (proposal: Proposal) => (
                <Link
                  to={`/artists/${proposal.artistSlug}`}
                  className="hover:text-foreground hover:underline"
                >
                  {proposal.artist}
                </Link>
              ),
            },
            {
              key: 'project',
              header: 'Project',
              secondary: true,
              cell: (proposal: Proposal) => (
                <Link
                  to={`/projects/${proposal.projectSlug}`}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  {proposal.project}
                </Link>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (proposal: Proposal) => (
                <Badge variant="outline" className={cn(proposalStatusStyles[proposal.status])}>
                  {proposal.status}
                </Badge>
              ),
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (proposal: Proposal) =>
                proposal.votes > 0 ? proposal.votes.toLocaleString() : '—',
            },
            {
              key: 'updated',
              header: 'Updated',
              align: 'right',
              secondary: true,
              cell: (proposal: Proposal) => (
                <span className="text-muted-foreground">{proposal.updated}</span>
              ),
            },
          ]}
          actions={(proposal) => (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                aria-label={`Actions for ${proposal.title}`}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <MoreHorizontal className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => openEdit(proposal)}>
                  <Pencil />
                  Edit
                </DropdownMenuItem>
                {proposal.archived ? (
                  <DropdownMenuItem onSelect={() => handleRestore(proposal)}>
                    <ArchiveRestore />
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => handleArchive(proposal)}>
                    <Archive />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPOSAL_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>

      {dialogOpen && (
        <ProposalDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialogOpen(false);
          }}
          mode={editing ? 'edit' : 'create'}
          initialValues={
            editing
              ? {
                  title: editing.title,
                  projectId: editing.projectId,
                  project: editing.project,
                  artistId: editing.artistId,
                  artist: editing.artist,
                  status: editing.status,
                  description: editing.description,
                  coverMedia: editing.coverMedia ?? null,
                  gallery: editing.gallery ?? [],
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
